import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';

import { MIN_SEARCH_KEYWORD_LENGTH } from '@/common/common.contants';
import MultiSearch from '@/components/molecules/MultiSearch/MultiSearch';
import PopularBrands from '@/components/molecules/PopularBrands/PopularBrands';
import SearchHistory from '@/components/molecules/SearchHistory/SearchHistory';
import {
  BrandSkeleton,
  TopBrandSkeleton,
} from '@/components/molecules/SkeletonLoader/BrandSkeleton/BrandSkeleton';
import TrendingNow from '@/components/molecules/TrendingNow/TrendingNow';
import { config } from '@/configs/env';
import { useLayout } from '@/contexts/LayoutContext';
import { useAnalyticsEvents } from '@/hooks/useAnalyticsEvents';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  useMultiSearchActions,
  useNativeMultiSearchModal,
} from '@/hooks/useMultiSearchActions';
import { useNavigateToPlp } from '@/hooks/useNavigateToPlp';
import { usePathBuilder } from '@/hooks/usePathBuilder';
import { useSearchData } from '@/hooks/useSearchContext';
import { useSearchEnterKey } from '@/hooks/useSearchEnterKey';
import { useSearchStartMessages } from '@/hooks/useSearchStartMessages';
import { useSearchSuggestions } from '@/hooks/useSearchSuggestions';
import { isUnauthorized } from '@/lib/api-service';
import { getCartService } from '@/services/cart.service';
import SendBrowserEvent from '@/services/SendBrowserEvent.service';
import { Brand } from '@/types';
import { IFRAME_POSTMESSAGE_TYPE } from '@/types/post-message.types';
import { RecommendationType } from '@/types/recommendation.types';
import { SearchInputRef, SearchStartProps } from '@/types/search.types';
import {
  getCurrentPageNameFromCookie,
  SessionStorageAnalyticProps,
  setSessionData,
  SourceEvent,
  TriggerType,
} from '@/utils/analytics.util';
import { navigateBarcodeScanner } from '@/utils/barcode.util';
import { getCookieOrSessionId } from '@/utils/cart.util';
import { BrowserEvent, IS_FROM_BRANDS_AND_KEYWORD } from '@/utils/constants';
import { navigateOrNotifyParent } from '@/utils/navigation.util';
import { savePlpFilterState } from '@/utils/plpFilterStorage.util';
import {
  isInIframe as getIsInIframe,
  postMessageToWeb,
  revealApp,
} from '@/utils/window.util';

import NativeSearchBar from './NativeSearchBar';
import {
  BrandsSectionWrapper,
  RecommendationsSectionWrapper,
  SearchContentContainer,
  SearchPageContainer,
  SearchStartContainer,
  TrendingSectionWrapper,
} from './SearchStart.styles';

import { createPortal } from 'react-dom';

const BRANDS_SECTION_MIN_HEIGHT_PX = 200;
const TRENDING_SECTION_MIN_HEIGHT_PX = 100;
const RECOMMENDATIONS_SECTION_MIN_HEIGHT_PX = 260;

// const NativeSearchBar = dynamic(() => import('./NativeSearchBar'), {
//   ssr: false,
//   loading: () => null,
// });

const MultiSearchModal = dynamic(() => import('@storefront-core/multisearch'), {
  ssr: false,
  loading: () => null,
});

const RecommendationCarousel = dynamic(
  () => import('@/components/molecules/Recommendation/RecommendationCarousel'),
  { ssr: false, loading: () => null }
);

const SearchAutoComplete = dynamic(
  () => import('../SearchAutoComplete/SearchAutoComplete'),
  {
    ssr: false,
    loading: () => null,
  }
);

const SearchStart: React.FC<SearchStartProps> = ({
  query,
  isNative = false,
}) => {
  // Custom hooks
  const router = useRouter();
  const pathname = usePathname();
  const { buildSearchPagePath } = usePathBuilder();

  const { topBrands, topKeywords, isBrandApiLoading, setCartData } =
    useSearchData();
  const {
    trackSearchPerform,
    trackMultiSearchPerformClick,
    trackMultiSearchClearAllClick,
    trackListSearchDismissedClick,
  } = useAnalyticsEvents();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { deviceType } = useLayout();
  const smallDeviceType = deviceType !== 'desktop';
  // useState
  const [trimmedValue, setTrimmedValue] = useState((query || '').trim());
  const [openMultiSearch, setOpenMultiSearch] = useState(false);
  const [storedQueries, setStoredQueries] = useState<string[]>([]);
  const [shouldRenderRecommendations, setShouldRenderRecommendations] =
    useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const barcodeQuery = (router.query.barcodeQuery as string) || '';
  const lang = pathname.split('/')[1] || 'en';
  const {
    suggestion,
    productSuggestion,
    isSearchingProducts,
    isInitialSearching,
    productSuggestionCount,
    loadMore,
    hasMore,
  } = useSearchSuggestions(trimmedValue, isNative, barcodeQuery);

  const searchInputRef = useRef<SearchInputRef | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const isInIframe = useMemo(() => getIsInIframe(), []);

  const isTopPicksRecommendationEnabled = config.TOP_PICKS_RECOMMENDATION;
  const pageRef = useRef<HTMLDivElement | null>(null);
  const isIOSWebView =
    (window as Window & { IS_IOS_WEBVIEW?: boolean }).IS_IOS_WEBVIEW === true;
  useEffect(() => {
    // Safety net: index.tsx reveals the app in the loading callback (skeleton),
    // but if that was skipped for any reason, reveal here too.
    revealApp();
    // Notify the web host parent (iframe context) that the MFE is ready.
    SendBrowserEvent.iframeReady();
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;

    if (!vv) return;

    const update = () => {
      const height = window.innerHeight - vv.height - (vv.offsetTop || 0);

      // ignore tiny changes (safe threshold)
      const finalHeight = height > 50 ? height : 0;

      setKeyboardHeight(finalHeight);
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('resize', update);

    update();

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    if (isNative) return;
    const sendHeight = () => {
      if (!pageRef.current) return;
      if (trimmedValue.length >= MIN_SEARCH_KEYWORD_LENGTH)
        SendBrowserEvent.setAutoCompleteIframeHeight(
          pageRef.current.scrollHeight
        );
    };
    sendHeight();
    const resizeObserver = new ResizeObserver(sendHeight);
    if (pageRef.current) resizeObserver.observe(pageRef.current);
    return () => resizeObserver.disconnect();
  }, [isNative, pageRef, trimmedValue]);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        if (await isUnauthorized()) return;
        const storeId = getCookieOrSessionId();

        const response = await getCartService(storeId);
        if (response.data) {
          setCartData(response.data);
        }
      } catch (error: unknown) {
        console.error('Error fetching cart:', error);
      }
    };
    // real-time sync product update from plp to query suggestion
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      switch (data?.type) {
        case IFRAME_POSTMESSAGE_TYPE.UPDATE_CART:
          fetchCart();
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [setCartData]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Trigger a single "page enter" transition on mount.
    // requestAnimationFrame ensures the initial styles paint before transitioning.
    const raf = window.requestAnimationFrame(() => setAnimateIn(true));
    const mountRecommendations = () => {
      setTimeout(() => {
        setShouldRenderRecommendations(true);
      });
    };

    // If the page is already loaded, mount immediately.
    if (document.readyState === 'complete') {
      mountRecommendations();
      return;
    }

    // Otherwise wait for full page load.
    window.addEventListener('load', mountRecommendations, { once: true });
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('load', mountRecommendations);
    };
  }, []);

  useEffect(() => {
    if (isNative) {
      const timer = setTimeout(() => {
        sessionStorage.removeItem(IS_FROM_BRANDS_AND_KEYWORD);
        searchInputRef.current?.focus();
        searchInputRef.current?.setSelectionToEnd();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isNative]);

  const searchEnter = () => {
    setSessionData(SessionStorageAnalyticProps.SEARCH_PERFORM_ANALYTIC_PROPS, {
      sourceEvent: SourceEvent.MANUAL_SEARCH,
      triggerType: TriggerType.ENTER,
      querySuggestionRecordFound: suggestion,
    });
  };
  const navigateToPLP = useNavigateToPlp({
    lang,
    isNative,
    isInIframe,
    router,
  });

  useNativeMultiSearchModal(openMultiSearch, isNative);
  useSearchStartMessages({
    onSearchQuery: setTrimmedValue,
    onStoredQueries: setStoredQueries,
  });

  useEffect(() => {
    const queryFromUrl = (router.query.q as string) || '';
    // setting the value of search state in case of back navigation
    const setSearchQuery = () => {
      setTrimmedValue(queryFromUrl);
    };
    setSearchQuery();
  }, [router.query.q]);

  const setAutocompelteHistory = (value: string) => {
    // pushing autocomplete url into history for back navigation
    const autoCompleteUrl = buildSearchPagePath({
      query: value.trim(),
      isNative,
      fromPlp: true,
      params: {
        ...(router.query.navigateToScreen
          ? {
              navigateToScreen: router.query.navigateToScreen as string,
              ...(barcodeQuery ? { barcodeQuery } : {}),
            }
          : {}),
      },
    });

    if (!isIOSWebView) {
      window.history.pushState(
        { ...window.history.state, url: autoCompleteUrl, as: autoCompleteUrl },
        '',
        autoCompleteUrl
      );
    }
  };

  useSearchEnterKey(trimmedValue, (query) => {
    if (openMultiSearch) return;
    searchEnter();
    setAutocompelteHistory(trimmedValue);
    navigateToPLP(query);
  });

  const {
    handleMultiSearch,
    handleMultilistSearchClick,
    handleClearAll,
    handleQueriesChange,
  } = useMultiSearchActions({
    lang,
    isNative,
    isInIframe,
    navigateToPLP,
    router,
    setOpenMultiSearch,
    setStoredQueries,
  });

  const handleSuggestionClick = (
    selectedSuggestion: string,
    index: number,
    suggestions: string[]
  ) => {
    savePlpFilterState({ currentPage: 1, dynamicFacets: [] });
    sessionStorage.removeItem(IS_FROM_BRANDS_AND_KEYWORD);
    setSessionData(SessionStorageAnalyticProps.SEARCH_PERFORM_ANALYTIC_PROPS, {
      sourceEvent: SourceEvent.QUERY_SUGGESTIONS,
      triggerType: TriggerType.DIRECT_CLICK,
      typedQuery: trimmedValue,
      querySuggestionSelected: selectedSuggestion,
      querySuggestionSelectedIndex: index,
      querySuggestionRecordFound: suggestions,
    });
    navigateOrNotifyParent({
      value: selectedSuggestion,
      eventType: 'SUGGESTION_CLICKED',
      payload: { value: selectedSuggestion },
      onNavigate: navigateToPLP,
      postMessageToParent: postMessageToWeb,
      isInIframe,
    });
  };

  const handleSeeAllClick = () => {
    const pageName = getCurrentPageNameFromCookie();
    setSessionData(SessionStorageAnalyticProps.SEARCH_PERFORM_ANALYTIC_PROPS, {
      sourceEvent: SourceEvent.PRODUCT_SUGGESTION,
      triggerType: TriggerType.DIRECT_CLICK,
      pageName: pageName,
    });
    navigateOrNotifyParent({
      value: trimmedValue,
      eventType: 'SEE_ALL_CLICKED',
      payload: { query: trimmedValue },
      onNavigate: navigateToPLP,
      postMessageToParent: postMessageToWeb,
      isInIframe,
    });
  };

  const handleSearchInputChange = (value: string) => {
    setTrimmedValue(value);
    sessionStorage.removeItem(IS_FROM_BRANDS_AND_KEYWORD);
  };

  const goBack = () => {
    const isBarcodeNavigation = navigateBarcodeScanner({
      router,
      shouldHandle:
        !!barcodeQuery || (!trimmedValue && !!router.query.navigateToScreen),
      screenToNavigate: router.query.navigateToScreen,
    });
    if (isBarcodeNavigation) return;

    if (!trimmedValue) {
      window.location.replace(config.MOBILE_SHOPPING_URL);
    } else {
      setTrimmedValue('');
      router.push(
        buildSearchPagePath({
          isNative,
          params: {
            ...(!!router.query.navigateToScreen
              ? {
                  navigateToScreen: router.query.navigateToScreen as string,
                }
              : {}),
          },
        })
      );
    }
  };

  const onTextClear = () => {
    sessionStorage.removeItem(IS_FROM_BRANDS_AND_KEYWORD);
    setTrimmedValue('');
    const routeToNavigate = buildSearchPagePath({
      isNative,
      params: {
        ...(!!router.query.navigateToScreen
          ? {
              navigateToScreen: router.query.navigateToScreen as string,
            }
          : {}),
      },
    });
    if (router.query.navigateToScreen) router.replace(routeToNavigate);
    else router.push(routeToNavigate);
  };

  const onBrandClick = (brand: Brand) => {
    setSessionData(SessionStorageAnalyticProps.SEARCH_PERFORM_ANALYTIC_PROPS, {
      sourceEvent: SourceEvent.POPULAR_BRANDS,
      triggerType: TriggerType.DIRECT_CLICK,
      sponsored: brand.sponsored,
    });

    if (brand.sponsored && brand.flexiPageHandle) {
      trackSearchPerform({
        query: brand.brand,
        page: 1,
      });

      if (isInIframe) {
        postMessageToWeb({
          type: BrowserEvent.FLEXI_PAGE_NAVIGATION,
          url: brand.flexiPageHandle,
        });
      } else {
        const brandDepotPath =
          config.MOBILE_SHOPPING_URL + brand.flexiPageHandle;
        window.location.href = brandDepotPath;
      }
    } else {
      savePlpFilterState({ currentPage: 1, dynamicFacets: [] });

      setSessionData(IS_FROM_BRANDS_AND_KEYWORD, true);
      setAutocompelteHistory(brand.brand);
      navigateToPLP(brand.brand);
    }
  };

  const handleHistoryClick = (value: string) => {
    setSessionData(SessionStorageAnalyticProps.SEARCH_PERFORM_ANALYTIC_PROPS, {
      sourceEvent: SourceEvent.HISTORY_CLIKED,
      triggerType: TriggerType.DIRECT_CLICK,
    });
    setAutocompelteHistory(value);
    navigateToPLP(value);
  };
  const handleTrendingKeywordClick = async (value: string) => {
    const analyticsEvents = {
      sourceEvent: SourceEvent.TRENDING_WORDS,
      triggerType: TriggerType.DIRECT_CLICK,
    };
    setSessionData(
      SessionStorageAnalyticProps.SEARCH_PERFORM_ANALYTIC_PROPS,
      analyticsEvents
    );
    setSessionData(IS_FROM_BRANDS_AND_KEYWORD, true);
    savePlpFilterState({ currentPage: 1, dynamicFacets: [] });
    setAutocompelteHistory(value);
    navigateToPLP(value);
  };

  useEffect(() => {
    if (!openMultiSearch) return;
    const body = document.body;
    const previousPosition = body.style.position;
    const previousOverflow = body.style.overflow;
    const previousWidth = body.style.width;
    body.style.position = 'fixed';
    body.style.overflow = 'hidden';
    body.style.width = '100%';
    return () => {
      body.style.position = previousPosition;
      body.style.overflow = previousOverflow;
      body.style.width = previousWidth;
    };
  }, [openMultiSearch]);

  const renderAutocomplete = () => {
    return (
      <SearchPageContainer
        keyboardBottomInset={keyboardHeight}
        isNative={isNative}
        animateIn={animateIn}
        isAutoComplete={true}
      >
        {
          <div ref={pageRef}>
            <SearchAutoComplete
              keyword={trimmedValue}
              suggestions={suggestion}
              productSuggestion={productSuggestion}
              isNative={isNative}
              onSuggestionClick={(selectedSuggestion: string, index: number) =>
                handleSuggestionClick(selectedSuggestion, index, suggestion)
              }
              onSeeAllClick={handleSeeAllClick}
              productSuggestionCount={productSuggestionCount}
              loadMore={loadMore}
              hasMore={hasMore}
              isSearchingProducts={isSearchingProducts}
              isInitialSearching={isInitialSearching}
            />
          </div>
        }
      </SearchPageContainer>
    );
  };

  return (
    <SearchStartContainer animateIn={animateIn}>
      {/* Show searchbar when isNative is true */}
      {isNative && (
        <NativeSearchBar
          trimmedValue={trimmedValue}
          onBack={goBack}
          onChange={handleSearchInputChange}
          placeholder={t('search.searchForProductText')}
          searchInputRef={searchInputRef}
          showClearIcon={true}
          onClear={onTextClear}
        />
      )}
      {trimmedValue.length >= MIN_SEARCH_KEYWORD_LENGTH ? (
        renderAutocomplete()
      ) : (
        <SearchPageContainer
          keyboardBottomInset={keyboardHeight}
          isNative={isNative}
          animateIn={animateIn}
          isAutoComplete={false}
        >
          {/* Search Multiple Items */}
          <MultiSearch onSearchClick={handleMultilistSearchClick} />
          <SearchContentContainer>
            {/* Search History */}
            <SearchHistory
              onClickHistory={handleHistoryClick}
              isNative={isNative}
              isWebMobileView={smallDeviceType}
            />

            {/* Stabilize layout: reserve space so skeleton->content doesn't reflow. */}
            <BrandsSectionWrapper
              minHeightPx={
                isBrandApiLoading || topBrands.length > 0
                  ? BRANDS_SECTION_MIN_HEIGHT_PX
                  : undefined
              }
            >
              {isBrandApiLoading && <BrandSkeleton />}
              {!isBrandApiLoading && topBrands.length > 0 && (
                <PopularBrands
                  popularBrands={topBrands}
                  isNative={isNative}
                  onClickBrand={onBrandClick}
                  isWebMobileView={smallDeviceType}
                />
              )}
            </BrandsSectionWrapper>

            <TrendingSectionWrapper
              minHeightPx={
                isBrandApiLoading || topKeywords.length > 0
                  ? TRENDING_SECTION_MIN_HEIGHT_PX
                  : undefined
              }
            >
              {isBrandApiLoading && <TopBrandSkeleton />}
              {!isBrandApiLoading && topKeywords.length > 0 && (
                <TrendingNow
                  trendingKeywords={topKeywords}
                  isNative={isNative}
                  onClickTrendingKeyword={handleTrendingKeywordClick}
                  isWebMobileView={smallDeviceType}
                />
              )}
            </TrendingSectionWrapper>
            {/* Reserve space to avoid a layout "jerk" when recommendations mount after page load */}
            {isTopPicksRecommendationEnabled && (
              <RecommendationsSectionWrapper
                minHeightPx={RECOMMENDATIONS_SECTION_MIN_HEIGHT_PX}
              >
                {shouldRenderRecommendations && (
                  <RecommendationCarousel
                    lang={lang}
                    isNative={isNative}
                    type={RecommendationType.SEARCH_START_TOPPICKS}
                  />
                )}
              </RecommendationsSectionWrapper>
            )}
          </SearchContentContainer>
        </SearchPageContainer>
      )}
      {openMultiSearch &&
        createPortal(
          <MultiSearchModal
            open={openMultiSearch}
            onClose={() => {
              setOpenMultiSearch(false);
              trackListSearchDismissedClick(isNative);
            }}
            isNative={isMobile}
            isNativeApp={isNative}
            onSearch={(queries: string[]) => {
              handleMultiSearch(queries);
              trackMultiSearchPerformClick(isNative);
            }}
            onQueriesChange={handleQueriesChange}
            onClearAll={() => {
              handleClearAll();
              trackMultiSearchClearAllClick(isNative);
            }}
            initialQueries={storedQueries}
            headerTitleText={t('multisearch.headerTitleText')}
            clearAllText={t('multisearch.clearAllText')}
            searchAllText={t('multisearch.searchAllText')}
            helperText={t('multisearch.helperText')}
            errorText={t('multisearch.errorText')}
            label={t('multisearch.label')}
            placeholderText={t('multisearch.placeholderText')}
          />,
          document.body
        )}
    </SearchStartContainer>
  );
};

export default SearchStart;
